import React, { useState, useRef } from 'react';
import { HiOutlinePhotograph, HiX } from 'react-icons/hi';
import { photoAPI, creatorAPI } from '../services/api';
import { getFriendlyError } from '../utils/errorMessage';
import './PostComposer.css';

/**
 * Facebook-style composer: start with "What's on your mind?", then write text,
 * optionally attach a photo, and publish. Supports text-only, photo-only, or
 * both. Only rendered for eligible (female) members by the parent.
 */
const PostComposer = ({ onPosted, authorName, authorPhotoUrl }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('Please choose an image.');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB.');
      return;
    }
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const removePhoto = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCaption('');
    setError('');
    setOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const publish = async () => {
    const text = caption.trim();
    if (!text && !file) {
      setError('Write something or add a photo to post.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      let contentUrl = null;
      if (file) {
        const uploadRes = await photoAPI.uploadCreatorContent(file);
        contentUrl = uploadRes.data.url;
      }
      // PHOTO when an image is attached, otherwise a text POST.
      const type = file ? 'PHOTO' : 'POST';
      await creatorAPI.uploadContent(type, contentUrl, null, text, false);
      reset();
      if (onPosted) onPosted();
    } catch (err) {
      setError(getFriendlyError(err, 'Could not publish your post. Please try again.'));
    } finally {
      setPosting(false);
    }
  };

  const initial = (authorName || '?').charAt(0).toUpperCase();

  return (
    <div className="post-composer">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={pickFile}
        style={{ display: 'none' }}
        id="post-composer-file"
      />

      {!open ? (
        <div className="post-composer-prompt">
          <div className="post-composer-avatar">
            {authorPhotoUrl ? (
              <img src={authorPhotoUrl} alt={authorName || 'You'} />
            ) : (
              <div className="post-composer-avatar-fallback">{initial}</div>
            )}
          </div>
          <button
            type="button"
            className="post-composer-trigger"
            onClick={() => setOpen(true)}
          >
            What&apos;s on your mind?
          </button>
          <button
            type="button"
            className="post-composer-photo-shortcut"
            onClick={() => { setOpen(true); setTimeout(() => fileInputRef.current && fileInputRef.current.click(), 0); }}
            aria-label="Add photo"
          >
            <HiOutlinePhotograph aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div className="post-composer-open">
          <div className="post-composer-head">
            <strong>Create post</strong>
            <button type="button" className="post-composer-close" onClick={reset} aria-label="Cancel">
              <HiX />
            </button>
          </div>

          <textarea
            className="post-composer-caption"
            placeholder="What's on your mind?"
            value={caption}
            maxLength={1000}
            autoFocus
            onChange={(e) => setCaption(e.target.value)}
          />

          {preview && (
            <div className="post-composer-preview">
              <button
                type="button"
                className="post-composer-preview-remove"
                onClick={removePhoto}
                aria-label="Remove photo"
              >
                <HiX />
              </button>
              <img src={preview} alt="Preview" />
            </div>
          )}

          {error && <div className="post-composer-error" role="alert">{error}</div>}

          <div className="post-composer-actions">
            <button
              type="button"
              className="post-composer-addphoto"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={posting}
            >
              <HiOutlinePhotograph aria-hidden="true" />
              {preview ? 'Change photo' : 'Add photo'}
            </button>
            <button
              type="button"
              className="post-composer-publish"
              onClick={publish}
              disabled={posting}
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostComposer;
